import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Tooltip, Paper, Typography, Chip, IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import axiosInstance from '../../../lib/axios';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PropTypes from 'prop-types';
import BillingDetailsModal from '../../Ragistration/BConfirmation/ACCGenerateDetailsModal';
import { toast } from 'react-toastify';

// Debounce utility to limit API calls
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const gridStyles = `
  .ag-theme-alpine {
    --ag-border-color: #c8c8c8;
    --ag-header-background-color: #f8f9fa;
    --ag-odd-row-background-color: #fcfcfc;
    --ag-border-radius: 6px;
  }
  .ag-theme-alpine .ag-cell {
    border-right: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    align-items: center;
    padding-left: 4px !important;
    padding-right: 4px !important;
    overflow: hidden;
  }
 
  .ag-theme-alpine .ag-header-cell {
    border-right: 1px solid #c8c8c8;
    border-bottom: 1px solid #c8c8c8;
    font-weight: 600;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }
 
  .ag-theme-alpine .ag-floating-filter-input {
    border-radius: 4px;
    height: 24px;
    font-size: 0.8rem;
  }
 
  .ag-theme-alpine .ag-floating-filter-input:focus {
    outline: none;
    border-color: #3f51b5;
    box-shadow: 0 0 0 2px rgba(63, 81, 181, 0.2);
  }
 
  .ag-theme-alpine .ag-header-cell::after {
    display: none;
  }
 
  .ag-theme-alpine .ag-ltr .ag-cell-first-right-pinned {
    border-left: 1px solid #c8c8c8;
  }
 
  .ag-floating-filter-button {
    margin-left: 0px !important;
  }
 
  .ag-theme-alpine .ag-cell .truncated-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    width: 100%;
    display: block;
  }
 
  .status-chip {
    height: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }
 
  .ag-input-wrapper:before {
    background-color: white;
    display: none;
  }
 
  .ag-theme-alpine .ag-floating-filter input[type="date"] {
    padding-left: 0 !important;
  }
 
  :where(.ag-ltr) .ag-header-cell-resize {
    right: -6px !important;
  }
  /* Hide checkbox column */
  .ag-theme-alpine .ag-selection-checkbox {
    display: none !important;
  }
  /* Mobile-specific pagination styling */
  @media (max-width: 600px) {
    .ag-paging-panel {
      display: flex;
      flex-direction: column; /* Stack items vertically */
      align-items: center;
      gap: 2px; /* Reduced gap for tighter layout */
      padding: 4px 8px; /* Reduced padding */
      font-size: 0.75rem;
      min-height: 60px; /* Reduced to minimize top space */
      position: relative;
      box-sizing: border-box; /* Ensure padding is included in height */
    }
    .ag-paging-button {
      padding: 4px 8px;
      min-width: 28px;
      height: 28px;
      line-height: 28px;
    }
    .ag-paging-row-summary-panel {
      order: 2;
      width: 100%;
      text-align: center;
      margin-bottom: 2px;
    }
    .ag-paging-page-size {
      display: none;
    }
    .total-records-mobile {
      order: 1; /* Place Total Records at the top */
      width: 100%;
      text-align: center;
      font-weight: 500;
      font-size: 0.75rem;
      margin: 0; /* Remove margins to keep inside panel */
      padding: 2px 0; /* Minimal padding for alignment */
      color: #333;
    }
  }
`;

const TruncatedCellRenderer = (props) => {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const cellRef = React.useRef(null);
  useEffect(() => {
    if (cellRef.current) {
      const isTextOverflowed =
        cellRef.current.scrollWidth > cellRef.current.clientWidth ||
        (props.value && props.value.length > 20);
      setIsOverflowed(isTextOverflowed);
    }
  }, [props.value]);
  const cellValue = props.valueFormatted ? props.valueFormatted : props.value;
  return (
    <Tooltip
      title={isOverflowed ? cellValue : ''}
      placement="top"
      arrow
      disableHoverListener={!isOverflowed}
    >
      <div
        ref={cellRef}
        className="truncated-cell"
        style={{ width: '100%' }}
      >
        {cellValue}
      </div>
    </Tooltip>
  );
};

const StatusCellRenderer = (props) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Booked':
        return '#ff9800';
      case 'Confirmed':
        return '#2196f3';
      case 'CheckedIn':
        return '#4caf50';
      case 'CheckedOut':
        return '#9e9e9e';
      case 'Cancel':
        return '#f44336';
      default:
        return '#757575';
    }
  };
  return (
    <Chip
      label={props.value}
      className="status-chip"
      sx={{
        backgroundColor: getStatusColor(props.value),
        color: 'white',
        width: '100%',
        maxWidth: '100px',
      }}
    />
  );
};

const ActionCellRenderer = ({ data, onViewAccount }) => {
  return (
    <Tooltip title="View Account Statement" arrow>
      <IconButton
        size="small"
        onClick={() => {
          // console.log('ActionCellRenderer: Row clicked, data:', data);
          onViewAccount(data);
        }}
        sx={{ color: '#635bff' }}
      >
        <AccountBalanceIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const getColumnDefs = (isMobile, onViewAccount) => [
  {
    headerName: 'BookNo',
    field: 'DocNo',
    width: isMobile ? 80 : 120,
    filter: 'agTextColumnFilter',
    cellRenderer: TruncatedCellRenderer,
    checkboxSelection: false,
  },
  {
    headerName: 'BookDt',
    field: 'FormattedDocDt',
    width: isMobile ? 80 : 100,
    filter: 'agTextColumnFilter',
    cellRenderer: TruncatedCellRenderer,
  },
  {
    headerName: 'Property',
    field: 'PropName',
    width: isMobile ? 120 : 250,
    filter: 'agTextColumnFilter',
    cellRenderer: TruncatedCellRenderer,
  },
  {
    headerName: 'ResName',
    field: 'ResidentName',
    width: isMobile ? 120 : 250,
    filter: 'agTextColumnFilter',
    cellRenderer: TruncatedCellRenderer,
  },
  {
    headerName: 'CheckinDt',
    field: 'FormattedCheckinDt',
    width: isMobile ? 130 : 100,
    filter: 'agTextColumnFilter',
    cellRenderer: TruncatedCellRenderer,
  },
  {
    headerName: 'CheckoutDt',
    field: 'FormattedCheckoutDt',
    width: isMobile ? 130 : 100,
    filter: 'agTextColumnFilter',
    cellRenderer: TruncatedCellRenderer,
  },
  {
    headerName: 'Mobile',
    field: 'MobileNo',
    width: isMobile ? 100 : 100,
    filter: 'agTextColumnFilter',
    cellRenderer: TruncatedCellRenderer,
  },
  {
    headerName: 'Email',
    field: 'Email',
    width: isMobile ? 130 : 200,
    filter: 'agTextColumnFilter',
    cellRenderer: TruncatedCellRenderer,
  },
  {
    headerName: 'Status',
    field: 'DBFLAG',
    width: isMobile ? 100 : 120,
    filter: 'agTextColumnFilter',
    cellRenderer: StatusCellRenderer,
    cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
  },
  {
    headerName: 'AccGen',
    field: 'Status',
    width: isMobile ? 80 : 60,
    filter: 'agTextColumnFilter',
    cellRenderer: TruncatedCellRenderer,
  },
  {
    headerName: 'Net Amount',
    field: 'NetAmt',
    width: isMobile ? 80 : 100,
    filter: 'agNumberColumnFilter',
    cellRenderer: TruncatedCellRenderer,
  },
];

function ResidentAccTable({
  searchBy = 'mobile',
  searchText = '',
  selectedProperty = null,
  fromDate = '',
  toDate = '',
  searchType = 'single',
  status = 'CurrentOccupied',
  triggerSearch = 0,
  setRows,
  setLoading,
  onViewAccount,
  setSearchClicked,
}) {
  const [gridRows, setGridRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [pageSize, setPageSize] = useState(50); // Default page size
  const [currentPage, setCurrentPage] = useState(1); // Track current page
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const prevTriggerSearchRef = useRef(0);
  const isFetchingRef = useRef(false); // Prevent multiple API calls
  const [openBillingModal, setOpenBillingModal] = useState(false);
  const [billingDetails, setBillingDetails] = useState(null);
  const [payAdvice, setPayAdvice] = useState(null);
  const [summaryReport, setSummaryReport] = useState(null);
  const [receiptSummary, setReceiptSummary] = useState(null);
  const [payGenerate, setPayGenerate] = useState(null);
  const [pendingDetails, setPendingDetails] = useState(null);
  const [planDataState, setPlanDataState] = useState(null);

  // Debug: Log props
  // console.log('ResidentAccTable props:', {
  //   searchBy,
  //   searchText,
  //   selectedProperty,
  //   fromDate,
  //   toDate,
  //   searchType,
  //   status,
  //   triggerSearch,
  //   setRows,
  //   setLoading,
  //   onViewAccount,
  //   setSearchClicked,
  // });

  useEffect(() => {
    if (triggerSearch === 0) {
      setGridRows([]);
      setRows([]);
    }
  }, [searchBy, searchType, searchText, selectedProperty, fromDate, toDate]);

  const fetchTableData = useCallback(
    debounce(async () => {
      if (isFetchingRef.current) {
        // console.log('fetchTableData: Already fetching, skipping...');
        return;
      }
      if (searchType === 'single' && (!searchText || searchText.trim() === '')) {

        setGridRows([]);
        setRows([]);
        setIsLoading(false);
        setLoading(false);
        return;
      }
      isFetchingRef.current = true;
      setIsLoading(true);
      setLoading(true);
      try {
        let flag = '';
        const apiUrl = 'AccDashboard/AccDashboard';
        if (searchType === 'single') {
          switch (searchBy) {
            case 'mobile':
              flag = 'ByResidentMobile';
              break;
            case 'name':
              flag = 'ByResidentName';
              break;
            case 'email':
              flag = 'ByResidentEmail';
              break;
            default:
              flag = 'ByResidentMobile';
          }
        } else {
          switch (status) {
            case 'CurrentOccupied':
              flag = 'Occupied';
              break;
            case 'PendingChecking':
              flag = 'Pend Checkin';
              break;
            case 'CheckingPending':
              flag = 'Checkout';
              break;
            default:
              flag = 'Occupied';
          }
        }
        const payload = {
          PropId: selectedProperty && selectedProperty !== 'All' ? selectedProperty : 2,
          FromDt: searchType === 'single' ? '' : (fromDate || '2025-09-10'),
          ToDt: searchType === 'single' ? '' : (toDate || '2025-09-30'),
          Flag: flag,
          start: (currentPage - 1) * pageSize + 1, // Calculate start index
          PageSize: pageSize,
          SearchText: searchType === 'single' ? (searchText || '') : '',
        };

        const response = await axiosInstance.post(apiUrl, payload);

        if (response.data.Status === 0) {
          const formattedRows = response.data.Data.map((obj) => ({
            ...obj,
            FormattedDocDt: obj.DocDt ? moment(obj.DocDt).format('DD-MM-YYYY') : '',
            FormattedCheckinDt: obj.CheckinDt && obj.CheckinDt !== '0001-01-01T00:00:00' ? moment(obj.CheckinDt).format('DD-MM-YYYY') : '',
            FormattedCheckoutDt: obj.CheckoutDt && obj.CheckoutDt !== '0001-01-01T00:00:00' ? moment(obj.CheckoutDt).format('DD-MM-YYYY') : '',
          }));

          setGridRows(formattedRows);
          setRows(formattedRows);
          if (gridApi) {
            setTimeout(() => {
              gridApi.sizeColumnsToFit();
            }, 0);
          }
        } else {

          setGridRows([]);
          setRows([]);
        }
      } catch (error) {
        console.error('API call failed:', error);
        setGridRows([]);
        setRows([]);
        toast.error('Failed to fetch data');
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
        setLoading(false);
      }
    }, 500), // Debounce for 500ms
    [searchBy, searchText, selectedProperty, fromDate, toDate, searchType, status, pageSize, currentPage, gridApi, setRows, setLoading]
  );

  const handleBillingDetails = async (rowData) => {
    if (!rowData.BookId) {
      toast.info("No transaction is visible");
      return;
    }

    try {
      setIsLoading(true);

      const response = await axiosInstance.post(
        'TrnBooking/GetReportTrnBooking',
        {
          "BookId": rowData.BookId,
          "Flag": "R"
        }
      );

      if (response.data.Status === 0 && response.data.Data) {
        setBillingDetails(response.data.Data);
        setOpenBillingModal(true);

        if (rowData.RoomPlanId) {
          await fetchPlanDataState(rowData.RoomPlanId);
        }

        await Promise.all([
          fetchPayAdvice(rowData.BookId),
          fetchPayGenerate(rowData.BookId),
          fetchPendingDetails(rowData.BookId),
          fetchSummaryReport(rowData.BookId),
          fetchReceiptReport(rowData.BookId),
        ]);
      } else {
        toast.error('No billing details found');
      }
    } catch (error) {
      console.error('Error fetching billing details:', error);
      toast.error('Error fetching billing details');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPlanDataState = async (PlanId) => {
    if (!PlanId || PlanId === "null" || PlanId === null) {
      // console.warn("Invalid RoomPlanId:", PlanId);
      return;
    }

    try {
      const response = await axiosInstance.post(`MstRoomPlan/RetriveMstRoomPlan`, {
        "RoomPlanID": PlanId,
        "Flag": "R"
      });

      if (response.data.Status === 0) {
        setPlanDataState(response.data.Data);
      }
    } catch (error) {
      console.error('Error fetching plan data:', error);
    }
  };

  const fetchPayAdvice = async (BookId) => {
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const response = await axiosInstance.post(`TrnBooking/GetSelectTrnPytAdv`, {
        PytAdvDt: currentDate,
        Flag: "BookingWise",
        BookId: BookId
      });

      if (response.data.Status === 0) {
        setPayAdvice(response.data.Data);
      }
    } catch (error) {
      console.error('Error fetching payment advice:', error);
    }
  };

  const fetchPayGenerate = async (BookId) => {
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const response = await axiosInstance.post(`TrnBooking/GetSelectTrnPytAdv`, {
        PytAdvDt: currentDate,
        Flag: "GenPytAdv",
        BookId: BookId
      });

      if (response.data.Status === 0) {
        setPayGenerate(response.data.Data);
      }
    } catch (error) {
      console.error('Error fetching pay generate data:', error);
    }
  };

  const fetchSummaryReport = async (BookId) => {
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const response = await axiosInstance.post(`TrnBooking/GetSelectTrnPytAdv`, {
        PytAdvDt: currentDate,
        Flag: "GenAccSum",
        BookId: BookId
      });

      if (response.data.Status === 0) {
        setSummaryReport(response.data.Data);
      }
    } catch (error) {
      console.error('Error fetching summary report:', error);
    }
  };

  const fetchReceiptReport = async (BookId) => {
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const response = await axiosInstance.post('BookingReports/GetBookingReportsDetail', {
        BookDtFrom: currentDate,
        BookUpToDt: currentDate,
        PropId: "0",
        Flag: "PyAdvWiseRecVchr",
        Id: BookId
      });

      if (response.data.Status === 0) {
        setReceiptSummary(response.data.Data);
      }
    } catch (error) {
      console.error('Error fetching receipt report:', error);
    }
  };

  const fetchPendingDetails = async (BookId) => {
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const response = await axiosInstance.post(`TrnBooking/GetSelectTrnPytAdv`, {
        Flag: "UnBilledAdvPyt",
        BookId: BookId,
        PytAdvDt: currentDate
      });

      if (response.data.Status === 0) {
        setPendingDetails(response.data.Data);
      }
    } catch (error) {
      console.error('Error fetching pending details:', error);
    }
  };

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    filterParams: {
      suppressAndOrCondition: true,
    },
    cellStyle: {
      borderRight: '1px solid #e0e0e0',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
    headerClass: 'ag-header-cell',
    suppressSizeToFit: true,
  }), []);

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = gridStyles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    if (prevTriggerSearchRef.current !== triggerSearch) {
      fetchTableData();
      prevTriggerSearchRef.current = triggerSearch;
    }
  }, [triggerSearch, fetchTableData]);

  useEffect(() => {
    if (triggerSearch === 0 && gridRows.length > 0) {

      setGridRows([]);
      setRows([]);
    }
  }, [triggerSearch, gridRows, setRows]);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const handleRowClick = (event) => {

    setSelectedRow(event.data);
    onViewAccount(event.data);
    handleBillingDetails(event.data);
  };

  const onPaginationChanged = useCallback((params) => {
    const newPageSize = params.api.paginationGetPageSize();
    const newPage = params.api.paginationGetCurrentPage() + 1; // AG-Grid uses 0-based index
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1); // Reset to first page on page size change
      fetchTableData();
    } else if (newPage !== currentPage) {
      setCurrentPage(newPage);
      fetchTableData();
    }
  }, [fetchTableData, pageSize, currentPage]);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', pt: 0, background: 'rgb(249, 249, 249)' }}>
      <Paper
        elevation={2}
        sx={{
          height: 'calc(100vh - 250px)',
          width: '100%',
          flexGrow: 1,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          className="ag-theme-alpine"
          style={{
            height: '100%',
            width: '100%',
            overflowX: 'auto',
          }}
        >
          <AgGridReact
            columnDefs={getColumnDefs(isMobile, onViewAccount)}
            rowData={gridRows}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={50}
            paginationPageSizeSelector={[50, 100, 500, 1000, 5000]}
            onRowClicked={handleRowClick}
            enableCellTextSelection={true}
            ensureDomOrder={true}
            loading={isLoading}
            rowHeight={isMobile ? 28 : 30}
            headerHeight={isMobile ? 28 : 30}
            floatingFiltersHeight={isMobile ? 26 : 28}
            overlayLoadingTemplate={
              '<span class="ag-overlay-loading-center">Loading data...</span>'
            }
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">No data found</span>'
            }
            suppressMenuHide={true}
            sideBar={false}
            suppressHorizontalScroll={false}
            onPaginationChanged={onPaginationChanged}
          />
        </div>
      </Paper>
      {/* {isMobile ? (
        <Typography className="total-records-mobile">
          Total Records: {gridRows.length}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 'medium', position: 'relative', bottom: '22px', left: '15px' }}>
          Total Records: {gridRows.length}
        </Typography>
      )} */}
      {!isMobile && (
        <Typography variant="body2" sx={{ fontWeight: 'medium', position: 'relative', bottom: '22px', left: '15px' }}>
          Total Records: {gridRows.length}
        </Typography>
      )}

      {selectedRow && (
        <BillingDetailsModal
          open={openBillingModal}
          onClose={() => setOpenBillingModal(false)}
          billingData={billingDetails}
          planDataState={planDataState}
          payAdvice={payAdvice}
          payGenerate={payGenerate}
          pendingDetails={pendingDetails}
          summaryReport={summaryReport}
          receiptSummary={receiptSummary}
        />
      )}
    </Box>
  );
}

ResidentAccTable.propTypes = {
  searchBy: PropTypes.string,
  searchText: PropTypes.string,
  selectedProperty: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])]),
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  searchType: PropTypes.string,
  status: PropTypes.string,
  triggerSearch: PropTypes.number.isRequired,
  setRows: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  onViewAccount: PropTypes.func.isRequired,
  setSearchClicked: PropTypes.func.isRequired,
};

export default ResidentAccTable;